import { Box, Button, HStack, Image } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useEffect, useRef } from "react";
import { TbView360Number } from "react-icons/tb";
import { PannellumViewer } from "../../models/PanoramicPhoto";
import { useStores } from "../../stores/RootStore";
import { useZoomPan } from "../../utils/image-zoom-utils";
import { CustomDialog } from "../CustomDialog";
import { NodeNavControls } from "../NodeNavControls/NodeNavControls";
import { getModalConfig, saveModalPosition } from "../../utils/panoramic-utils";

const NodePhotoModalBase = ({ onClose }: { onClose: () => void }) => {
  const { osaNodeStore } = useStores();
  const node = osaNodeStore.getSelected()!;
  const isPanoramic = !!node.photo?.panoramic;
  const src = node.photo?.url || "";

  const zoomPan = useZoomPan(true);
  const panoRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<PannellumViewer | null>(null);

  useEffect(() => {
    if (!isPanoramic || !src || !panoRef.current) return;

    saveModalPosition(viewerRef.current);
    viewerRef.current?.destroy();
    viewerRef.current = null;
    panoRef.current.innerHTML = "";

    const viewer = window.pannellum.viewer(
      panoRef.current,
      getModalConfig({
        type: "equirectangular",
        panorama: src,
        autoLoad: true,
        showZoomCtrl: false,
        showFullscreenCtrl: false,
        mouseZoom: true,
        draggable: true,
      }),
    );

    viewerRef.current = viewer;

    const syncView = () => saveModalPosition(viewer);
    viewer.on("mouseup", syncView);
    viewer.on("touchend", syncView);
    viewer.on("zoomchange", syncView);

    requestAnimationFrame(() => viewer.resize());

    return () => {
      saveModalPosition(viewerRef.current);
      viewerRef.current?.destroy();
      viewerRef.current = null;
    };
  }, [isPanoramic, src, node.id]);

  return (
    <CustomDialog
      size="cover"
      onClose={onClose}
      header={node.name}
      footer={<NodeNavControls />}
    >
      {isPanoramic ? (
        <Box position="relative" width="100%" height="80vh">
          <Box
            ref={panoRef}
            width="100%"
            height="100%"
            bg="black"
            borderRadius="md"
          />
          <Box
            position="absolute"
            top={4}
            right={4}
            zIndex={10}
            display="flex"
            alignItems="center"
          >
            <TbView360Number
              size="2rem"
              color="white"
              style={{ opacity: 0.7, pointerEvents: "none" }}
            />
          </Box>
        </Box>
      ) : (
        <Box
          ref={zoomPan.containerRef}
          onWheel={zoomPan.handleWheel}
          onMouseDown={zoomPan.handleMouseDown}
          cursor={zoomPan.scale === 1 ? "default" : "grab"}
          overflow="hidden"
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="relative"
        >
          <Image
            ref={zoomPan.imgRef}
            src={src}
            objectFit="contain"
            maxW="100%"
            maxH="80vh"
            transform={`scale(${zoomPan.scale}) translate(${zoomPan.translate.x}px, ${zoomPan.translate.y}px)`}
            transition="transform 0.1s"
            draggable={false}
          />
          {zoomPan.scale !== 1 && (
            <HStack
              position="absolute"
              top={4}
              right={4}
              bg="rgba(0,0,0,0.6)"
              color="white"
              p={2}
              borderRadius="md"
            >
              {Math.round(zoomPan.scale * 100)}%
              <Button
                colorPalette="primary"
                ml={2}
                size="sm"
                fontSize="sm"
                onClick={zoomPan.reset}
              >
                Reset
              </Button>
            </HStack>
          )}
        </Box>
      )}
    </CustomDialog>
  );
};

export const NodePhotoModal = observer(NodePhotoModalBase);
